
import { Directive, EmbeddedViewRef, Input, OnInit, TemplateRef, ViewContainerRef } from "@angular/core";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AccountFeatureService, FeatureAccessType } from "@services/accountFeature.service";
import { AccountOption } from "@services/class/AccountOption";
import { SessionService } from "@services/session.service";
import { SettingsName } from "../../class/SettingsName";

export interface IIfEnabledSettings {
    accountOption?: string;
    userRoles?: string[];
    readFeatures?: AccountFeatures[];
    manageFeatures?: AccountFeatures[];
    requireAllRoles?: boolean;
}

@Directive({
    selector: '[ifEnabled]'
})
export class IfEnabledDirective implements OnInit {

    @Input() set ifEnabled(settings: IIfEnabledSettings) { this.settings = settings; }

    settings: IIfEnabledSettings;

    embeddedViewRef: EmbeddedViewRef<unknown>;

    constructor(
        private readonly sessionService: SessionService,
        private readonly accountFeatureService: AccountFeatureService,
        private templateRef: TemplateRef<unknown>,
        private viewContainerRef: ViewContainerRef
    ) { }

    ngOnInit(): void {
        this.createView();
        this.process();
    }

    // ---

    private createView(): void {
        if (this.embeddedViewRef) {
            return;
        }

        this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.templateRef);
    }

    private clearView(): void {
        this.viewContainerRef.clear();

        if (!this.embeddedViewRef) {
            return;
        }

        this.embeddedViewRef = null;
    }

    private process(): void {
        if (!this.hasRequirements()) {
            console.warn("No requirements passed into IfEnabledDirective");
            return;
        }

        let removeElement = false;

        if (this.requireRemoveDueToFeatureAccess() || this.requireRemoveDueToUserRole()) {
            removeElement = true;
        }

        if (removeElement) {
            this.clearView();
        }
    }

    private hasRequirements(): boolean {
        if (!this.settings) {
            return false;
        }

        return !!this.settings.accountOption
            || !!this.settings.userRoles?.length
            || !!this.settings.readFeatures?.length
            || !!this.settings.manageFeatures?.length;
    }

    private requireRemoveDueToFeatureAccess(): boolean {
        const requiresReadFeatures = this.settings.readFeatures?.length;
        const requiresManageFeatures = this.settings.manageFeatures?.length;

        if (!requiresReadFeatures && !requiresManageFeatures) {
            return false
        }


        let hasMissingReadFeature = false;
        let hasMissingManageFeature = false;

        if (requiresReadFeatures) {
            hasMissingReadFeature = !this.accountFeatureService.currentUserHasAllFeatureAccess(this.settings.readFeatures, FeatureAccessType.Read);
        }

        if (requiresManageFeatures) {
            hasMissingManageFeature = !this.accountFeatureService.currentUserHasAllFeatureAccess(this.settings.manageFeatures, FeatureAccessType.Manage);
        }

        return hasMissingReadFeature || hasMissingManageFeature;
    }

    private requireRemoveDueToUserRole(): boolean {
        const requiresUserRole = this.settings.userRoles?.length;

        if (!requiresUserRole && !this.settings.accountOption) {
            return false
        }

        let removeElement = false;

        if (requiresUserRole) {
            removeElement = !this.accountFeatureService.currentUserHasRole(this.settings.userRoles, this.settings.requireAllRoles);
        }

        if (this.settings.accountOption) {
            const accountOptions = this.sessionService.getItem<AccountOption>(SettingsName.AccountOptions);

            removeElement = !accountOptions[this.settings.accountOption];
        }

        return removeElement;
    }

    private getRootElement(): HTMLElement {
        if (!this.embeddedViewRef?.rootNodes?.length) {
            return;
        }

        return this.embeddedViewRef.rootNodes[0];
    }
}
